import type { PickByValue, PickByValueExact } from 'utility-types'
import React, { type FC, type PropsWithChildren, useEffect, useRef } from 'react'
import { Box, type SxProps, type Theme } from '@mui/material'

import type { ReadonlyRecord } from '../../../utils'

import { type AppState, ConnectwareError, ConnectwareErrorType, type Translation } from '../../../domain'

import type { Usecases } from '../../../application'

import { CircularLoader, DetailsHeader } from '../common'
import { ErrorMessage } from '../ErrorMessage'
import { useTranslator } from '../Internationalization'
import { useId, useRouting } from '../routing'
import { useAppState, useAppUsecase } from '../State'
import { RecordPanelModals, useRecordPanelContent } from './Panel'

const wrapperStyle: SxProps<Theme> = { mt: 2, width: '100%' }
const panelActionsStyle: SxProps<Theme> = { display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }

export type ResourceDetailsModalsProps = Readonly<{ leave: VoidFunction }>

type Props<Resource> = Readonly<{
    /**
     * The use case the details will subscribe to
     *
     * It expects the name of the usecase that has the subscribe method AND yields a unsub function
     */
    subscriptionUsecase: keyof PickByValue<Usecases, { subscribe: (id: string) => VoidFunction | void }>

    /**
     * Should yield the single resource
     */
    resourceSelector: (s: AppState) => Resource | ConnectwareError | null

    /**
     * In case the resource is not found, it will display this as the title
     */
    notFoundTitle: Translation

    /**
     * The header information of the resource
     */
    infoTitle: Translation
    infoBody: Translation

    /**
     * The header title of the resource
     */
    headerResourceName: Translation
    headerName: keyof PickByValueExact<Resource, string>
    headerActions: FC<{ resource: Resource }>

    /**
     * The header details of the resource
     */
    headerDetails: FC<{ resource: Resource }>

    /**
     * The content of the resource
     */
    content: FC<{ resource: Resource }>

    /**
     * The modals to manage the resource
     */
    modals: FC<ResourceDetailsModalsProps>
}>

/**
 * The details of the resource of the current route: on its page, or in the record panel it's rendered in
 *
 * In a panel, the panel's title names the resource, so the header is only its actions, without the documentation
 */
export const ResourceDetails = <Resource extends ReadonlyRecord<string, unknown> & Readonly<{ id: string }>>({
    resourceSelector,
    subscriptionUsecase,
    notFoundTitle,
    modals: Modals,
    headerResourceName,
    headerName,
    headerActions: HeaderActions,
    infoTitle,
    infoBody,
    headerDetails: HeaderDetails,
    content: Content,
}: PropsWithChildren<Props<Resource>>): ReturnType<FC> => {
    const id = useId()
    const routing = useRouting()
    const panel = useRecordPanelContent()

    const resource = useAppState(resourceSelector)
    const translator = useTranslator()

    const subscription = useAppUsecase(subscriptionUsecase)

    /**
     * Subscribe to resource changes
     */
    useEffect(() => subscription.subscribe(id), [subscription, id])

    /** In a panel, until the subscription resets it, the state can still hold the previous record's resource */
    const current = resource !== null && !ConnectwareError.is(resource) && (panel === null || resource.id === id) ? resource : null
    const lastFound = useRef<Resource | null>(null)
    lastFound.current = current ?? lastFound.current

    /** In a panel, a resource found before and then not found anymore (e.g. deleted from the list) stays shown while the panel leaves */
    const gone = panel !== null && lastFound.current !== null && ConnectwareError.isOfTypes(resource, ConnectwareErrorType.NOT_FOUND)
    const shown = current ?? (gone ? lastFound.current : null)
    const title = shown === null ? null : String(shown[headerName])

    /**
     * The panel's facts about its record, reported up from here because only the details know the resource
     *
     * TODO: CC-5041 (https://cybusio.atlassian.net/browse/CC-5041) - let the panel read its record itself, so these effects go
     */
    useEffect(() => {
        if (gone) {
            panel?.dismiss()
        }
    }, [gone, panel])

    useEffect(() => {
        if (panel && title !== null) {
            panel.showTitle(title)
        }
    }, [panel, title])

    if (shown === null) {
        return ConnectwareError.is(resource)
            ? (
                <ErrorMessage
                    data-testid="resource-details-error"
                    customTitle={(e) =>
                        ConnectwareError.isOfTypes(e, ConnectwareErrorType.NOT_FOUND) ? translator.formatTranslation(notFoundTitle, { id }) : null}
                    error={resource}
                    stack
                    extras="section"
                />
            )
            : <CircularLoader />
    }

    if (panel) {
        return (
            <>
                <Box data-testid="resource-details-panel-actions" sx={panelActionsStyle}>
                    <HeaderActions resource={shown} />
                </Box>
                <HeaderDetails resource={shown} />
                <Box sx={wrapperStyle}>
                    <Content resource={shown} />
                </Box>
                <RecordPanelModals id={id}>
                    <Modals leave={panel.close} />
                </RecordPanelModals>
            </>
        )
    }

    return (
        <>
            <DetailsHeader
                title={[headerResourceName, { count: 1 }]}
                titleAppendix={String(shown[headerName])}
                titleActions={<HeaderActions resource={shown} />}
                description={[infoTitle, infoBody]}
            />
            <HeaderDetails resource={shown} />
            <Box sx={wrapperStyle}>
                <Content resource={shown} />
            </Box>
            <Modals leave={() => routing.redirectToParent()} />
        </>
    )
}
